import { useEffect, useState } from 'react';

/**
 * Read the resolved theme off the `dark` class on `<html>`, rather than off the
 * theme React context.
 *
 * **Writers use the context; readers use the class.** `AppearanceSection` calls
 * `setTheme`, so it genuinely needs `@/contexts/ThemeProvider`. Everything that
 * only wants to know "is it dark right now" should subscribe here instead, for
 * two reasons this codebase hit in practice:
 *
 * - **`mode: 'system'` never changes when the OS theme flips.** Consumers that
 *   keyed an effect on `theme.mode` simply did not re-run, even though
 *   `@ai-tutor/ui`'s `useTheme` had already swapped the class and every CSS
 *   variable under it. `MindspaceGraph` worked around this with its own
 *   `matchMedia` listener plus a three-branch resolve; watching the class makes
 *   that unnecessary, because the writer has already resolved `system` down to
 *   a concrete class by the time it lands.
 * - **`useTheme()` throws outside a `ThemeProvider`**, which made chart and
 *   graph components unmountable in Storybook. The catalog drives the theme by
 *   putting the class on `<html>` directly (`withThemeByClassName`), and
 *   mounting the real provider there is not an option — it would fight the
 *   toolbar for ownership of that same class.
 *
 * `MindspaceGraph`'s original docstring called the class "the canonical source"
 * and avoided reading it only because doing so "would require a
 * MutationObserver". That observer is here, written once.
 */
export function subscribeToThemeClass(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  });
  return () => observer.disconnect();
}

/** True when `<html>` currently carries the `dark` class. */
export function readIsDarkMode(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.classList.contains('dark');
}

/**
 * Subscribe to the resolved theme. Re-renders on every light/dark flip,
 * including one caused by an OS-level change while the app is on `system`.
 */
export function useIsDarkMode(): boolean {
  const [isDark, setIsDark] = useState(readIsDarkMode);

  useEffect(() => {
    const sync = () => setIsDark(readIsDarkMode());
    const unsubscribe = subscribeToThemeClass(sync);
    // The class may have changed between the initial `useState` and this
    // effect running.
    sync();
    return unsubscribe;
  }, []);

  return isDark;
}
